import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import { useTable, useFilters, usePagination, useSortBy } from "react-table";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/DataTable.css";
import { isAfter, isBefore, parseISO, compareAsc } from "date-fns";

const PLACEHOLDER_TOPICS = new Set(["topic1", "topic2"]);

function isPlaceholderTopic(t) {
  if (t == null || typeof t !== "string") return false;
  return PLACEHOLDER_TOPICS.has(t.trim().toLowerCase());
}
// AH -> The frontend was showing N/A for summaries
// function isUnavailableSummary(summary) {
//   if (summary == null || String(summary).trim() === "") return true;
//   return String(summary).toLowerCase().includes("not available");
// }
function isUnavailableSummary(summary) {
  if (summary == null || String(summary).trim() === "") return true;

  const clean = String(summary).trim().toLowerCase();

  return (
    clean === "not available" ||
    clean === "n/a" ||
    clean === "na" ||
    clean === "null" ||
    clean === "undefined"
  );
}

function displaySummary(summary) {
  return isUnavailableSummary(summary) ? "N/A" : String(summary);
}

function normalizeCountry(t) {
  const lower = String(t).trim().toLowerCase();
  if (lower === "us" || lower === "usa") return "US";
  return String(t).trim();
}

// fix this in database, appear as one tag -> for now I added an easy fix to split combined values on commas (type of publication only)
function splitCommaParts(value) {
  return String(value)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function typesAsArray(value) {
  const parts = [];
  if (Array.isArray(value)) {
    value.forEach((item) => parts.push(...splitCommaParts(item)));
  } else if (value == null || value === "") {
    return [];
  } else if (typeof value === "string") {
    const s = value.trim();
    if (s.startsWith("[")) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          parsed.forEach((item) => parts.push(...splitCommaParts(item)));
        }
      } catch {}
    } else {
      parts.push(...splitCommaParts(s));
    }
  }
  return [...new Set(parts)];
}

function listAsArray(value, mapPart = (t) => String(t).trim()) {
  const parts = [];
  if (Array.isArray(value)) {
    value.forEach((item) => {
      const s = mapPart(item);
      if (s) parts.push(s);
    });
  } else if (value == null || value === "") {
    return [];
  } else if (typeof value === "string") {
    const s = value.trim();
    if (s.startsWith("[")) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          parsed.forEach((item) => {
            const t = mapPart(item);
            if (t) parts.push(t);
          });
        }
      } catch {}
    } else {
      const t = mapPart(s);
      if (t) parts.push(t);
    }
  }
  return [...new Set(parts)];
}

function topicsAsArray(value) {
  return listAsArray(value).filter((t) => !isPlaceholderTopic(t));
}

function countriesAsArray(value) {
  return listAsArray(value, normalizeCountry);
}

function renderFilterTags(value, onTagClick, { toTags = topicsAsArray, skipPlaceholderTopics } = {}) {
  let arr = toTags(value);
  if (skipPlaceholderTopics) {
    arr = arr.filter((t) => !isPlaceholderTopic(t));
  }
  if (arr.length === 0) return "N/A";
  return arr.map((tag, i) => (
    <React.Fragment key={`${tag}-${i}`}>
      {i > 0 ? ", " : null}
      <span className="filter-tag" onClick={() => onTagClick(tag)}>
        {tag}
      </span>
    </React.Fragment>
  ));
}

function topicsMatchSearch(value, inputLower) {
  const arr = topicsAsArray(value).filter((t) => !isPlaceholderTopic(t));
  return arr.some((t) => t.toLowerCase().includes(inputLower));
}

function SummaryCell({ value }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const textRef = useRef(null);
  const text = displaySummary(value);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || text === "N/A") {
      setShowToggle(false);
      return;
    }
    if (isExpanded) {
      setShowToggle(true);
      return;
    }
    setShowToggle(el.scrollHeight > el.clientHeight + 1);
  }, [text, isExpanded]);

  if (text === "N/A") {
    return "N/A";
  }

  return (
    <div className="summary-cell">
      <div
        ref={textRef}
        className={
          isExpanded
            ? "summary-cell-text"
            : "summary-cell-text summary-cell-text--clamped"
        }
      >
        {text}
      </div>
      {showToggle && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="btn btn-link summary-cell-toggle"
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
}

const DataTableComponent = ({ data }) => {
  const filterTopicRef = useRef(() => {});
  const filterCountryRef = useRef(() => {});
  const filterTypeRef = useRef(() => {});
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [filteredData, setFilteredData] = useState(data);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  useEffect(() => {
    console.log(`Selected Countries:`, selectedCountries);
  }, [selectedCountries]);

  useEffect(() => {
    console.log(`Selected Types:`, selectedTypes);
  }, [selectedTypes]);

  useEffect(() => {
    console.log(`Selected Topics:`, selectedTopics);
  }, [selectedTopics]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        if (document.activeElement.id === "search-for") {
          handleSearch();
        } else if (
          document.activeElement.id === "date-from" ||
          document.activeElement.id === "date-to"
        ) {
          applyDateFilter();
        }
      }
    }; 

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [searchInput, startDate, endDate]);

  const handleSearch = () => {
    globalSearch(searchInput, data);
  };

  const containsSubstring = (list, input) => {
    const lowerCaseInput = input.toLowerCase();
    const arr = Array.isArray(list) ? list : list == null ? [] : [list];
    return arr.some(
      (item) => item && String(item).toLowerCase().includes(lowerCaseInput)
    );
  };

  const globalSearch = (input, data) => {
    if (input) {
      const q = input.toLowerCase();
      const filtered = data.filter((row) => {
        return (
          (!isUnavailableSummary(row.summary) &&
            String(row.summary).toLowerCase().includes(q)) ||
          String(row.source_name || "")
            .toLowerCase()
            .includes(q) ||
          String(row.country_of_publication || "")
            .toLowerCase()
            .includes(q) ||
          containsSubstring(row.author, input) ||
          containsSubstring(row.associated_orgs, input) ||
          containsSubstring(row.type_of_pub, input) ||
          topicsMatchSearch(row.topics, q)
        );
      });
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  };

  const dateBetweenFilterFn = (rows, id, filterValues) => {
    let [start, end] = filterValues.map((date) =>
      date ? parseISO(date) : null
    );

    if (!start && !end) {
      return rows;
    }

    return rows.filter((row) => {
      const rowDate = parseISO(row.values[id]);
      if (!rowDate) {
        return false;
      }
      if (start && end) {
        return isAfter(rowDate, start) && isBefore(rowDate, end);
      } else if (start) {
        return isAfter(rowDate, start);
      } else if (end) {
        return isBefore(rowDate, end);
      }
      return true;
    });
  };

  const typeInFilterFn = (rows, id, filterValues) => {
    if (!filterValues) {
      return rows;
    }

    return rows.filter((row) => {
      const types = row.values[id];
      if (!types) {
        return false;
      }
      let items =
        id === "topics"
          ? topicsAsArray(types)
          : id === "type_of_pub"
          ? typesAsArray(types)
          : id === "country_of_publication"
          ? countriesAsArray(types)
          : Array.isArray(types)
          ? types.map((item) => String(item).trim()).filter(Boolean)
          : typeof types === "string"
          ? [String(types).trim()].filter(Boolean)
          : [];
      if (items.length === 1) {
        return items.every((item) => filterValues.includes(item));
      } else {
        return items.some((item) => filterValues.includes(item));
      }
    });
  };

  const uniqueTopics = useMemo(() => {
    const topicsSet = new Set();
    data.forEach((row) => {
      topicsAsArray(row.topics).forEach((topic) => {
        if (!isPlaceholderTopic(topic)) topicsSet.add(topic);
      });
    });
    return Array.from(topicsSet).map((topic) => ({
      value: topic,
      label: topic,
    }));
  }, [data]);

  const uniqueTypes = useMemo(() => {
    const typeSet = new Set();
    data.forEach((row) => {
      typesAsArray(row.type_of_pub).forEach((type) => typeSet.add(type));
    });
    return Array.from(typeSet).map((type) => ({ value: type, label: type }));
  }, [data]);

  const uniqueCountries = useMemo(() => {
    const countrySet = new Set();
    data.forEach((row) => {
      countriesAsArray(row.country_of_publication).forEach((country) =>
        countrySet.add(country)
      );
    });
    return Array.from(countrySet).map((country) => ({
      value: country,
      label: country,
    }));
  }, [data]);

  const filterSelectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        borderColor: "#064322",
        boxShadow: state.isFocused
          ? "0 0 0 0.2rem rgba(6, 67, 34, 0.25)"
          : "none",
        "&:hover": { borderColor: "#043017" },
      }),
      placeholder: (base) => ({ ...base, color: "#333" }),
      multiValue: (base) => ({ ...base, backgroundColor: "#d4e5da" }),
      multiValueLabel: (base) => ({ ...base, color: "#064322", fontWeight: 500 }),
      multiValueRemove: (base) => ({
        ...base,
        color: "#064322",
        ":hover": {
          backgroundColor: "#064322",
          color: "#fff",
        },
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "#064322"
          : state.isFocused
          ? "rgba(6, 67, 34, 0.12)"
          : "#fff",
        color: state.isSelected ? "#fff" : "#212529",
      }),
      menu: (base) => ({
        ...base,
        border: "1px solid #064322",
        borderRadius: 4,
        overflow: "hidden",
      }),
      menuList: (base) => ({ ...base, padding: 0 }),
    }),
    []
  );

  const handleMultiSelectChange = (filterType, selectedOptions) => {
    const values = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];

    switch (filterType) {
      case "country_of_publication":
        setSelectedCountries(values);
        break;
      case "type_of_pub":
        setSelectedTypes(values);
        break;
      case "topics":
        setSelectedTopics(values);
        break;
      default:
        break;
    }

    setFilter(filterType, values.length > 0 ? values : undefined);
  };

  const applyDateFilter = () => {
    setFilter("date_of_pub", [startDate, endDate]);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Source Name",
        accessor: "source_name",
        Cell: ({ row }) => (
          <a href={row.original.link} target="_blank" rel="noopener noreferrer">
            {row.original.source_name}
          </a>
        ),
        sortType: "basic",
      },
      {
        Header: "Author",
        accessor: "author",
        Cell: ({ value }) => (Array.isArray(value) ? value.join(", ") : value),
        sortType: "basic",
      },
      {
        Header: "Date of Publication",
        accessor: "date_of_pub",
        filter: "dateBetween",
        sortType: (rowA, rowB, columnId) => {
          const rawA = rowA.values[columnId];
          const rawB = rowB.values[columnId];
          if (!rawA && !rawB) return 0;
          if (!rawA) return -1;
          if (!rawB) return 1;
          return compareAsc(parseISO(rawA), parseISO(rawB));
        },
      },
      {
        Header: "Country of Publication",
        accessor: "country_of_publication",
        Cell: ({ value }) =>
          renderFilterTags(value, (country) => filterCountryRef.current(country), {
            toTags: countriesAsArray,
          }),
        filter: "typeIn",
        sortType: "basic",
      },
      {
        Header: "Associated Organizations",
        accessor: "associated_orgs",
        Cell: ({ value }) => (Array.isArray(value) ? value.join(", ") : value),
        sortType: "basic",
      },
      {
        Header: "Type of Publication",
        accessor: "type_of_pub",
        Cell: ({ value }) =>
          renderFilterTags(value, (type) => filterTypeRef.current(type), {
            toTags: typesAsArray,
          }),
        filter: "typeIn",
        disableSortBy: true,
      },
      {
        Header: "Topics",
        accessor: "topics",
        Cell: ({ value }) =>
          renderFilterTags(value, (topic) => filterTopicRef.current(topic), {
            skipPlaceholderTopics: true,
          }),
        filter: "typeIn",
        disableSortBy: true,
      },
      {
        Header: "Summary",
        accessor: "summary",
        Cell: ({ value }) => <SummaryCell value={value} />,

        disableSortBy: true, // Disable sorting for the summary column
      },
    ],
    []
  );

  const filterTypes = useMemo(
    () => ({
      dateBetween: dateBetweenFilterFn,
      typeIn: typeInFilterFn,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { pageIndex, pageSize },
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
    prepareRow,
    setFilter,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 10 },
      filterTypes,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  filterTopicRef.current = (topic) =>
    handleMultiSelectChange("topics", [{ value: topic, label: topic }]);

  filterCountryRef.current = (country) =>
    handleMultiSelectChange("country_of_publication", [
      { value: country, label: country },
    ]);

  filterTypeRef.current = (type) =>
    handleMultiSelectChange("type_of_pub", [{ value: type, label: type }]);

  return (
    <div className="container database-table-controls">
      <div className="row mb-4">
        <i>
          Note: Summaries and dates for certain articles are not available yet —{" "}
          we are working on it! Additionally, note that some summaries may be
          AI-generated.{" "}
        </i>
      </div>
      <div className="row mb-4 g-2 database-date-filter-row">
        <div className="col-6 col-md-2">
          <label htmlFor="date-from">From Date:</label>
          <input
            id="date-from"
            type="date"
            className="form-control"
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-6 col-md-2">
          <label htmlFor="date-to">To Date:</label>
          <input
            id="date-to"
            type="date"
            className="form-control"
            placeholder="YYYY-MM-DD"
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-2 database-date-filter-apply">
          <button
            type="button"
            className="btn btn-database-filter w-100"
            onClick={applyDateFilter}
          >
            Apply Date Filter
          </button>
        </div>
      </div>
      <div className="row mb-4 g-2 database-multi-filter-row">
        <div className="col-md-4">
          <label htmlFor="country-filter">Filter by Country of Publication:</label>
          <Select
            inputId="country-filter"
            isMulti
            options={uniqueCountries}
            value={selectedCountries.map((c) => ({ value: c, label: c }))}
            onChange={(selectedOptions) =>
              handleMultiSelectChange("country_of_publication", selectedOptions)
            }
            styles={filterSelectStyles}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="type-filter">Filter by Type of Publication:</label>
          <Select
            inputId="type-filter"
            isMulti
            options={uniqueTypes}
            value={selectedTypes.map((t) => ({ value: t, label: t }))}
            onChange={(selectedOptions) =>
              handleMultiSelectChange("type_of_pub", selectedOptions)
            }
            styles={filterSelectStyles}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="topic-filter">Filter by Topic:</label>
          <Select
            inputId="topic-filter"
            isMulti
            options={uniqueTopics}
            value={selectedTopics.map((t) => ({ value: t, label: t }))}
            onChange={(selectedOptions) =>
              handleMultiSelectChange("topics", selectedOptions)
            }
            styles={filterSelectStyles}
          />
        </div>
      </div>
      <div className="row mb-4 database-toolbar-row">
        <div className="col-md-6 col-lg-5 d-flex align-items-center database-page-size-row">
          <label htmlFor="page-selection" className="mb-0">
            Publications Per Page:
          </label>
          <select
            id="page-selection"
            className="form-control database-page-size-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 40, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6 col-lg-7 d-flex justify-content-end database-toolbar-search">
          <div
            className="d-flex align-items-center database-search-wrap"
            style={{ whiteSpace: "nowrap" }}
          >
        <label htmlFor="search-for" className="sr-only">Search for content</label>
        <input
          id="search-for"
          type="text"
          size="large"
          name="searchInput"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search for content (e.g., topic, type, author)"
          className="form-control database-search-input"
        />
            <button
              type="button"
              className="btn btn-database-filter"
              onClick={handleSearch}
            >
              Apply Search
            </button>
          </div>
        </div>
        <div>
          <br></br>
          <b>Click on a header to sort the results!</b>
        </div>
      </div>
      <div className="database-table-scroll">
      <table {...getTableProps()} className="table table-hover table-striped">
        <thead className="thead-dark">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  <span className="table-header-label">
                    {column.render("Header")}
                  </span>
                  <div className="table-header-sort" aria-hidden={!column.isSorted}>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? column.id === "date_of_pub"
                          ? "(Descending)"
                          : "(Z - A)"
                        : column.id === "date_of_pub"
                          ? "(Ascending)"
                          : "(A - Z)"
                      : "\u00a0"}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="row mb-4 database-pagination-row">
        <div
          className="col-md-12 d-flex justify-content-end align-items-center"
          style={{ fontSize: "0.875em" }}
        >
          {filteredData.length > 0 ? (
            <div className="pagination">
              <button
                className="btn btn-outline-dark mr-2"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {"<<"}
              </button>{" "}
              <button
                className="btn btn-outline-dark mr-2"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"<"}
              </button>{" "}
              <button
                className="btn btn-outline-dark mr-2"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                {">"}
              </button>{" "}
              <button
                className="btn btn-outline-dark mr-2"
                onClick={() => gotoPage(pageOptions.length - 1)}
                disabled={!canNextPage}
              >
                {">>"}
              </button>{" "}
              <span className="align-middle ml-2">
                Page {pageIndex + 1} of {pageOptions.length}
              </span>
            </div>
          ) : (
            <span className="text-muted" style={{ fontStyle: "italic" }}>
              No results for current filters.
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataTableComponent;
